<script lang="ts">
	import { appStore } from '../stores/appStore';
	import { getTokenStorage, setTokenStorage } from '../utils/localStorage';
	import type { AppApiClient } from '../api_contracts/app.client';
	import Preloader from '../components/auth/Preloader.svelte';
	import DarkModeToggle from '../components/auth/DarkModeToggle.svelte';
	import BrandingSide from '../components/auth/BrandingSide.svelte';
	import BackToDashboard from '../components/auth/BackToDashboard.svelte';
	import { onMount } from 'svelte';

	// Handed over by OAuthCallback when a provider sign-in succeeded but no
	// account matched. We already know who they are, so the form only needs to
	// collect what the provider cannot give us.
	type OAuthPrefill = {
		email: string;
		firstName?: string;
		lastName?: string;
		avatarUrl?: string;
		provider?: string;
		providerId?: string;
		ts?: number;
	};

	const PREFILL_MAX_AGE_MS = 15 * 60 * 1000;

	let oauth = $state<OAuthPrefill | null>(null);
	let client: AppApiClient | null = $state(null);
	let email = $state('');
	let password = $state('');
	let confirmPassword = $state('');
	let userName = $state('');
	let firstName = $state('');
	let lastName = $state('');
	let country = $state('');
	let loading = $state(false);
	let error = $state('');
	let success = $state(false);
	let usernameChecking = $state(false);
	let usernameAvailable = $state<boolean | null>(null);
	let showPassword = $state(false);
	let showConfirmPassword = $state(false);

	// Check if user is already logged in
	$effect(() => {
		if (client) {
			const token = getTokenStorage();
			if (token) {
				(async () => {
					try {
						const result = await client.validateToken({ token });
						console.log('Token from localStorage:', token);
						if (result.response.successFull) {
							// Redirect to already logged in page
							window.location.hash = '/already-logged-in';
						} else {
							console.log('Token is invalid.');
							setTokenStorage(undefined);
							appStore.setToken('');
							// Stay on register page
						}
					} catch (e) {
						console.error('Error validating token:', e);
					}
				})();
			}
		}
	});

	// Subscribe to store
	appStore.subscribe((state) => {
		client = state.client;
	});

	// Derive a username suggestion from the email local part. The provider does
	// not give us a handle we can reuse (GitHub returns a numeric id, not the
	// login), and the field is editable anyway with a live availability check.
	function suggestUserName(address: string): string {
		const base = address.split('@')[0].toLowerCase().replace(/[^a-z0-9_]/g, '');
		return base.length >= 3 ? base : '';
	}

	onMount(() => {
		const raw = localStorage.getItem('oauth_registration_data');
		if (!raw) return;

		try {
			const data = JSON.parse(raw) as OAuthPrefill;

			// Ignore anything stale or unusable rather than prefilling a form with
			// a leftover identity from an abandoned sign-up.
			const stale = data.ts !== undefined && Date.now() - data.ts > PREFILL_MAX_AGE_MS;
			if (!data.email || stale) {
				localStorage.removeItem('oauth_registration_data');
				return;
			}

			oauth = data;
			email = data.email;
			firstName = data.firstName ?? '';
			lastName = data.lastName ?? '';
			userName = suggestUserName(data.email);
		} catch (e) {
			console.error('Discarding malformed oauth_registration_data:', e);
			localStorage.removeItem('oauth_registration_data');
		}
	});

	const providerLabel = $derived(
		oauth?.provider === 'google' ? 'Google' : oauth?.provider === 'github' ? 'GitHub' : 'your account'
	);

	// Debounce username check
	let usernameCheckTimeout: ReturnType<typeof setTimeout>;
	async function checkUsername() {
		if (!client || !userName || userName.length < 3) {
			usernameAvailable = null;
			return;
		}

		clearTimeout(usernameCheckTimeout);
		usernameCheckTimeout = setTimeout(async () => {
			try {
				usernameChecking = true;
				const result = await client!.checkUserNameIsAvailable({ userName });
				usernameAvailable = result.response.successFull;
			} catch (e) {
				console.error('Username check error:', e);
				usernameAvailable = null;
			} finally {
				usernameChecking = false;
			}
		}, 500);
	}

	$effect(() => {
		checkUsername();
	});

	async function handleRegister(e: Event) {
		e.preventDefault();

		if (!client) {
			error = 'Client not initialized';
			return;
		}

		// Validation
		if (!email || !password || !userName || !firstName || !lastName || !country) {
			error = 'Please fill in all required fields';
			return;
		}

		if (password !== confirmPassword) {
			error = 'Passwords do not match';
			return;
		}

		if (password.length < 8) {
			error = 'Password must be at least 8 characters';
			return;
		}

		if (usernameAvailable === false) {
			error = 'Username is already taken';
			return;
		}

		try {
			loading = true;
			error = '';

			const result = await client.register({
				email,
				password,
				userName,
				firstName,
				lastName,
				country
			});

			if (result.response.successFull) {
				success = true;
				// The handover is spent. The next provider sign-in matches this
				// account by email and links the identity server side.
				localStorage.removeItem('oauth_registration_data');
				// Store email for verification page
				localStorage.setItem('pendingVerificationEmail', email);
				// Don't auto-redirect, let user click to verify
			} else {
				error = result.response.reason || 'Registration failed';
			}
		} catch (e: any) {
			error = e.message || 'Error during registration';
			console.error('Registration error:', e);
		} finally {
			loading = false;
		}
	}
</script>

<Preloader />
<DarkModeToggle />

<div class="relative z-1 bg-white p-6 sm:p-0 dark:bg-gray-900">
	<div
		class="flex h-screen w-full flex-col justify-center sm:p-0 lg:flex-row dark:bg-gray-900"
	>
		<!-- Left Side: Form -->
		<div class="flex w-full flex-1 flex-col lg:w-1/2">
			<!-- <div class="mx-auto w-full max-w-md pt-5 sm:py-10">
				<BackToDashboard />
			</div> -->

			<div class="mx-auto flex w-full max-w-lg flex-1 flex-col justify-center">
				{#if success}
					<!-- Success State -->
					<div class="text-center py-8">
						<svg
							class="w-16 h-16 text-green-500 mx-auto mb-4"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
							/>
						</svg>
						<h2 class="text-2xl font-semibold text-gray-900 mb-2 dark:text-white/90">
							Check your email!
						</h2>
						<p class="text-sm text-gray-500 dark:text-gray-400 mb-4">
							We've sent a verification email to <strong>{email}</strong>
						</p>
						<p class="text-sm text-gray-500 dark:text-gray-400 mb-6">
							Click the link in the email or enter the 6-digit code to verify your account.
						</p>
						<a
							href="#/verify-email"
							class="inline-block bg-brand-500 hover:bg-brand-600 text-white px-6 py-3 rounded-lg font-medium transition"
						>
							Enter verification code
						</a>
					</div>
				{:else}
					<!-- Form State -->
					<div class="mb-5 sm:mb-8">
						<h1
							class="text-title-sm sm:text-title-md mb-2 font-semibold text-gray-800 dark:text-white/90"
						>
							{oauth ? 'Finish setting up' : 'Sign Up'}
						</h1>
						<p class="text-sm text-gray-500 dark:text-gray-400">
							{#if oauth}
								We got your details from {providerLabel}. Just pick a username, choose a
								country and set a password.
							{:else}
								Enter your email and password to sign up!
							{/if}
						</p>
					</div>

					<div>
						<!-- Provider handover banner. There are deliberately no social
						     buttons here: signing up with a provider IS signing in with it.
						     Login sends the user through the provider, and OAuthCallback
						     routes them here only when no account matched. A second set of
						     buttons on this page would just be a slower path to the same
						     place. -->
						{#if oauth}
							<div
								class="mb-6 flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-800 dark:bg-white/5"
							>
								{#if oauth.avatarUrl}
									<img
										src={oauth.avatarUrl}
										alt=""
										class="h-10 w-10 shrink-0 rounded-full"
									/>
								{/if}
								<div class="min-w-0">
									<p class="text-sm font-medium text-gray-800 dark:text-white/90">
										Signed in with {providerLabel}
									</p>
									<p class="truncate text-sm text-gray-500 dark:text-gray-400">
										{oauth.email}
									</p>
								</div>
							</div>
						{/if}

						<!-- Form -->
						<form onsubmit={handleRegister}>
							{#if error}
								<div
									class="mb-4 p-3 bg-red-50 border border-red-200 rounded-md dark:bg-red-900/20 dark:border-red-800"
								>
									<p class="text-sm text-red-600 dark:text-red-400">{error}</p>
								</div>
							{/if}

							<div class="space-y-5">
								<!-- Name Fields -->
								<div class="grid grid-cols-1 gap-5 sm:grid-cols-2">
									<div class="sm:col-span-1">
										<label
											for="firstName"
											class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-400"
										>
											First Name<span class="text-error-500">*</span>
										</label>
										<input
											type="text"
											id="firstName"
											bind:value={firstName}
											disabled={loading}
											placeholder="Enter your first name"
											class="shadow-theme-xs focus:border-brand-300 focus:ring-brand-500/10 dark:focus:border-brand-800 h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 placeholder:text-gray-400 focus:ring-3 focus:outline-hidden dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30"
											required
										/>
									</div>
									<div class="sm:col-span-1">
										<label
											for="lastName"
											class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-400"
										>
											Last Name<span class="text-error-500">*</span>
										</label>
										<input
											type="text"
											id="lastName"
											bind:value={lastName}
											disabled={loading}
											placeholder="Enter your last name"
											class="shadow-theme-xs focus:border-brand-300 focus:ring-brand-500/10 dark:focus:border-brand-800 h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 placeholder:text-gray-400 focus:ring-3 focus:outline-hidden dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30"
											required
										/>
									</div>
								</div>

								<!-- Username -->
								<div>
									<label
										for="userName"
										class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-400"
									>
										Username<span class="text-error-500">*</span>
									</label>
									<div class="relative">
										<input
											type="text"
											id="userName"
											bind:value={userName}
											oninput={checkUsername}
											disabled={loading}
											placeholder="john_doe"
											class="shadow-theme-xs focus:border-brand-300 focus:ring-brand-500/10 dark:focus:border-brand-800 h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 placeholder:text-gray-400 focus:ring-3 focus:outline-hidden dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30"
											required
										/>
										{#if usernameChecking}
											<span class="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-gray-400">Checking...</span>
										{:else if usernameAvailable === true}
											<span class="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-brand-500">✓ Available</span>
										{:else if usernameAvailable === false}
											<span class="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-error-500">✗ Taken</span>
										{/if}
									</div>
								</div>

								<!-- Email -->
								<div>
									<label
										for="email"
										class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-400"
									>
										Email<span class="text-error-500">*</span>
									</label>
									<input
										type="email"
										id="email"
										bind:value={email}
										disabled={loading}
										readonly={!!oauth}
										placeholder="Enter your email"
										class="shadow-theme-xs focus:border-brand-300 focus:ring-brand-500/10 dark:focus:border-brand-800 h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 placeholder:text-gray-400 focus:ring-3 focus:outline-hidden dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 {oauth
											? 'cursor-not-allowed bg-gray-50 text-gray-500 dark:bg-white/5 dark:text-gray-400'
											: ''}"
										required
									/>
									{#if oauth}
										<!-- Locked on purpose. The backend links a provider identity to
										     an existing account by matching this email. Change it here and
										     the link never happens, so every later provider sign-in
										     would land back on this page. -->
										<p class="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
											From your {providerLabel} account — this is how we recognise you
											next time you sign in.
										</p>
									{/if}
								</div>

								<!-- Country -->
								<div>
									<label
										for="country"
										class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-400"
									>
										Country<span class="text-error-500">*</span>
									</label>
									<select
										id="country"
										bind:value={country}
										disabled={loading}
										class="shadow-theme-xs focus:border-brand-300 focus:ring-brand-500/10 dark:focus:border-brand-800 h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 focus:ring-3 focus:outline-hidden dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
										required
									>
										<option value="">Select your country</option>
										<option value="US">United States</option>
										<option value="GB">United Kingdom</option>
										<option value="CA">Canada</option>
										<option value="AU">Australia</option>
										<option value="DE">Germany</option>
										<option value="FR">France</option>
										<option value="KE">Kenya</option>
										<option value="NG">Nigeria</option>
										<option value="ZA">South Africa</option>
										<option value="IN">India</option>
										<option value="JP">Japan</option>
										<option value="BR">Brazil</option>
										<option value="MX">Mexico</option>
										<option value="ES">Spain</option>
										<option value="IT">Italy</option>
										<option value="NL">Netherlands</option>
										<option value="SE">Sweden</option>
										<option value="NO">Norway</option>
										<option value="DK">Denmark</option>
										<option value="FI">Finland</option>
									</select>
								</div>

								<!-- Password -->
								<div>
									<label
										for="password"
										class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-400"
									>
										Password<span class="text-error-500">*</span>
									</label>
									<div class="relative">
										<input
											type={showPassword ? 'text' : 'password'}
											id="password"
											bind:value={password}
											disabled={loading}
											placeholder="Min 8 characters"
											class="shadow-theme-xs focus:border-brand-300 focus:ring-brand-500/10 dark:focus:border-brand-800 h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 pr-12 text-sm text-gray-800 placeholder:text-gray-400 focus:ring-3 focus:outline-hidden dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30"
											required
										/>
										<button
											type="button"
											onclick={() => showPassword = !showPassword}
											class="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 transition"
											tabindex="-1"
										>
											{#if showPassword}
												<!-- Eye Slash Icon (Hide) -->
												<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5">
													<path stroke-linecap="round" stroke-linejoin="round" d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88" />
												</svg>
											{:else}
												<!-- Eye Icon (Show) -->
												<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5">
													<path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
													<path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
												</svg>
											{/if}
										</button>
									</div>
								</div>

								<!-- Confirm Password -->
								<div>
									<label
										for="confirmPassword"
										class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-400"
									>
										Confirm Password<span class="text-error-500">*</span>
									</label>
									<div class="relative">
										<input
											type={showConfirmPassword ? 'text' : 'password'}
											id="confirmPassword"
											bind:value={confirmPassword}
											disabled={loading}
											placeholder="Re-enter password"
											class="shadow-theme-xs focus:border-brand-300 focus:ring-brand-500/10 dark:focus:border-brand-800 h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 pr-12 text-sm text-gray-800 placeholder:text-gray-400 focus:ring-3 focus:outline-hidden dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30"
											required
										/>
										<button
											type="button"
											onclick={() => showConfirmPassword = !showConfirmPassword}
											class="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 transition"
											tabindex="-1"
										>
											{#if showConfirmPassword}
												<!-- Eye Slash Icon (Hide) -->
												<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5">
													<path stroke-linecap="round" stroke-linejoin="round" d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88" />
												</svg>
											{:else}
												<!-- Eye Icon (Show) -->
												<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5">
													<path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
													<path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
												</svg>
											{/if}
										</button>
									</div>
								</div>

								<!-- Button -->
								<div>
									<button
										type="submit"
										disabled={loading || usernameAvailable === false}
										class="bg-brand-500 shadow-theme-xs hover:bg-brand-600 flex w-full items-center justify-center rounded-lg px-4 py-3 text-sm font-medium text-white transition disabled:bg-gray-400 disabled:cursor-not-allowed"
									>
										{loading ? 'Creating account...' : 'Sign Up'}
									</button>
								</div>
							</div>
						</form>

						<div class="mt-5">
							<p
								class="text-center text-sm font-normal text-gray-700 sm:text-start dark:text-gray-400"
							>
								Already have an account?
								<a href="#/login" class="text-brand-500 hover:text-brand-600 dark:text-brand-400"
									>Sign In</a
								>
							</p>
						</div>
					</div>
				{/if}
			</div>
		</div>

		<!-- Right Side: Branding -->
		<BrandingSide />
	</div>
</div>

<style>
	:global(body) {
		margin: 0;
		padding: 0;
	}
</style>
